import React, { useCallback, useEffect, useMemo, useRef, useContext, useState } from 'react'
import {
  Container,
  ProfilePhotoAction,
  ProfilePhotoActionIcon,
  ProfilePhotoCopy,
  ProfilePhotoFallback,
  ProfilePhotoFrame,
  ProfilePhotoImage,
  ProfilePhotoInner,
  ProfilePhotoSection,
  ProfilePhotoText,
  ProfilePhotoTitle,
  TherapistsContainer,
  TherapistsHeader,
  TherapistsTitle
} from './styled'
import { ButtonContainer, FormSpacer } from 'ui/styled'
import Button from 'components/Form/Button'
import FormCore from 'components/Form/Core'
import useI18n from 'hooks/useI18n'
import { industryOptions, optionsCountries, optionsGermanyZipcodes, optionsSalutation, optionsUsaStates } from 'utils/options'
import { CoreContext } from 'context/CoreContext'
import { apiRequest, exposeStrapiError, parseStrapiImage } from 'utils'
import { UpdateMe } from 'services/me'
import { toast } from 'react-toastify'
import UploadFile from 'components/Form/UploadFile'

export default function MyAccount() {

  const { t } = useI18n()

  const formRef = useRef()

  const { user, reloadMe } = useContext(CoreContext)

  const [register, setRegister] = useState(null)
  const [loading, setLoading] = useState(false)
  const [profileImage, setProfileImage] = useState(null)
  const [profilePreview, setProfilePreview] = useState(null)


  const updateRegister = useCallback(() => {
    const form = formRef?.current?.getForm(true)
    setRegister(previous => ({ ...previous, ...form }))
  }, [])

  const userFormItems = useMemo(() => ([
    {
      ref: 'salutation',
      placeholder: t("salutation"),
      options: optionsSalutation,
      required: true
    },
    {
      ref: 'firstName',
      placeholder: t("first_name"),
      required: true
    },
    {
      ref: 'lastName',
      placeholder: t("last_name"),
      required: true
    },
    {
      ref: 'companyName',
      placeholder: t("company_name"),
      half: true,
      required: true
    },
    {
      ref: 'jobTitle',
      placeholder: t("job_title"),
      half: true,
      required: true
    },
    {
      ref: 'industry',
      placeholder: t("industry"),
      options: industryOptions,
      half: true,
      required: true
    },
    {
      ref: 'country',
      placeholder: t("country"),
      options: optionsCountries,
      disabled: true,
      // required: true,
      half: true,
      onBlur: () => updateRegister()
    },
    ...(
      register?.country === "United States" ? [
        {
          ref: 'state',
          placeholder: t("state"),
          options: optionsUsaStates,
          full: true,
          required: true,
        }
      ] : register?.country === "germany" ? [
        {
          ref: 'zipcode',
          placeholder: t("zipcode"),
          options: optionsGermanyZipcodes,
          full: true,
          required: true
        }
      ] : [
        {
          ref: 'state',
          placeholder: t("state"),
          // options: [],
          half: true,
          required: true,
        },
        {
          ref: 'zipcode',
          placeholder: t("zipcode"),
          // options: [],
          half: true,
          required: true
        }
      ]
    ),
    {
      ref: 'email',
      placeholder: t("email"),
      half: true,
      required: true
    },
    {
      ref: 'phone',
      placeholder: t("phone_number"),
      half: true,
      type: 'phone',
      required: true
    },
    {
      ref: 'password',
      type: 'password',
      placeholder: t("new_password"),
      half: true
    },

    {
      ref: 'cpassword',
      type: 'password',
      placeholder: t("confirm_new_password"),
      half: true
    },
    {
      passwordValidation: true,
      row: true
    },
  ]), [t, register, updateRegister])

  const save = async () => {
    const form = formRef?.current?.getForm()
    if (!form) return;
    setLoading(true)
    const payload = {
      ...form,
      image: profileImage?.id || null,
    }
    const result = await apiRequest(() => UpdateMe(payload))
    if (result && !exposeStrapiError(result)) {
      await reloadMe()
      toast.success(t('updated_successfully'))
    }
    setLoading(false)
  }

  useEffect(() => {
    setRegister(user ? { ...user } : null)
    setProfileImage(user?.image || null)
    setProfilePreview(user?.image?.url ? parseStrapiImage(user.image.url) : null)
  }, [user])

  return (
    <>
      <FormSpacer extraLarge />
      <TherapistsContainer>
        <TherapistsHeader>
          <TherapistsTitle>{t('my_account')}</TherapistsTitle>
        </TherapistsHeader>
        <Container>
          <ProfilePhotoSection>
            <ProfilePhotoFrame>
              <ProfilePhotoInner>
                {profilePreview ? (
                  <ProfilePhotoImage image={profilePreview} />
                ) : (
                  <ProfilePhotoFallback>
                    {`${register?.firstName?.[0] || user?.firstName?.[0] || ''}${register?.lastName?.[0] || user?.lastName?.[0] || ''}`.trim() || 'U'}
                  </ProfilePhotoFallback>
                )}
              </ProfilePhotoInner>
              <UploadFile
                onChange={setProfileImage}
                onPreview={setProfilePreview}
                accept='image/*'
              >
                <ProfilePhotoAction>
                  <ProfilePhotoActionIcon />
                </ProfilePhotoAction>
              </UploadFile>
            </ProfilePhotoFrame>

            <ProfilePhotoCopy>
              <ProfilePhotoTitle>Profile photo</ProfilePhotoTitle>
              <ProfilePhotoText>
                Add or update your account image here. This photo will appear next to your name in the header.
              </ProfilePhotoText>
            </ProfilePhotoCopy>
          </ProfilePhotoSection>

          <TherapistsTitle small>{t('registration_data')}</TherapistsTitle>
          <FormCore ref={formRef} register={register} formItems={userFormItems} />
          <ButtonContainer end space>
            <Button
              small
              width={'fit-content'}
              color='primary'
              outline
              onClick={() => {
                setRegister(user ? { ...user } : null)
                setProfileImage(user?.image || null)
                setProfilePreview(user?.image?.url ? parseStrapiImage(user.image.url) : null)
              }}
            >
              {t('cancel')}
            </Button>
            <Button small color='blue' loading={loading} onClick={save} width={'fit-content'} >{t('save')}</Button>
          </ButtonContainer>
        </Container>
      </TherapistsContainer>
    </>
  )
}
